import PropTypes from 'prop-types'
import { Button } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { toPath } from '../../routes'
import codes from './codes'

// Width of antd's expand-icon column. The expanded details are indented by the
// same amount so their fields line up with the card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#6b6b6b',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
})

function Field({ label, children = null }) {
  const classes = useStyles()
  return (
    <div className={classes.field}>
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
}

// Codes are only meaningful next to their label, so the summary line prefixes
// each value. Empty codes are dropped to avoid a dangling separator.
function codeLine(pairs) {
  return pairs
    .filter(([, value]) => value)
    .map(([label, value]) => `${label}: ${value}`)
    .join(' · ')
}

// Always-visible summary shown for every device type row on mobile.
export function DeviceTypeMobileCard({ deviceType }) {
  const classes = useStyles()
  const { id, name } = deviceType

  // A device type bills on a 30-day cycle, a 90-day cycle, or both, so which
  // codes are filled in varies. Lead with the 30-day pair and fall back to the
  // 90-day one so the card never collapses to just a name.
  const summary =
    codeLine([
      ['CPT 30', deviceType.cptCodes30],
      ['ICD-10', deviceType.icd10Code],
    ]) ||
    codeLine([
      ['CPT 90', deviceType.cptCodes90Order2],
      ['ICD-10', deviceType.icd10Code90Order2],
    ])

  return (
    <div className={classes.card}>
      <div className={classes.name}>{name || '—'}</div>
      {summary && <div className={classes.line}>{summary}</div>}
      <div>
        <Link
          to={toPath.deviceTypes.deviceType.edit({ id })}
          className="edit-button"
        >
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ paddingLeft: 0 }}
          >
            Edit
          </Button>
        </Link>
      </div>
    </div>
  )
}

DeviceTypeMobileCard.propTypes = {
  deviceType: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    cptCodes30: PropTypes.string,
    cptCodes90Order2: PropTypes.string,
    icd10Code: PropTypes.string,
    icd10Code90Order2: PropTypes.string,
  }).isRequired,
}

// Every code column behind the per-row expand toggle. Driven off the same
// `codes` list as the desktop columns so the two can't drift apart.
export function DeviceTypeMobileDetails({ deviceType }) {
  const classes = useStyles()
  return (
    <div className={classes.detailsGrid}>
      {codes.map(({ name, attribute }) => (
        <Field key={attribute} label={name}>
          {deviceType[attribute] || '—'}
        </Field>
      ))}
    </div>
  )
}

DeviceTypeMobileDetails.propTypes = {
  deviceType: PropTypes.shape({}).isRequired,
}
